import React, { useState, useEffect } from 'react';
import axios from '../../services/api';
import { toast } from 'react-toastify';
import { FaCog, FaTrash, FaTimes } from 'react-icons/fa';
import AssignSipModal from './modals/AssignSipModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { useTranslation } from 'react-i18next';

const RoomSipAccount = () => {
  const { t } = useTranslation();
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedFloor, setSelectedFloor] = useState('All Floor');
  const [selectedSipStatus, setSelectedSipStatus] = useState('All SIP');
  const [rooms, setRooms] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [roomToRemoveSip, setRoomToRemoveSip] = useState(null);
  const [displayRooms, setDisplayRooms] = useState([]);
  const [searchKey, setSearchKey] = useState('');

  // Fetch communities on mount
  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await axios.get('/communities');
      setCommunities(response.data.communities);
    } catch (error) {
      console.error(error);
    }
  };

  // Update building list when community changes
  useEffect(() => {
    if (selectedCommunity) {
      fetchBuildings(selectedCommunity);
    } else {
      setSelectedBuilding('');
      setSelectedFloor('All Floor');
    }
  }, [selectedCommunity]);

  const fetchBuildings = async communityId => {
    try {
      const response = await axios.get(`/buildings/${communityId}`, { sort: 'buildingName' });
      setBuildings(response.data);
    } catch (error) {
      console.error(error);
    }
  };
  // Update building list when community changes
  useEffect(() => {
    if (selectedBuilding) {
      fetchFloors(selectedBuilding);
    } else {
      setSelectedFloor('All Floor');
    }
  }, [selectedBuilding]);

  const fetchFloors = async buildingId => {
    try {
      const response = await axios.get(`/rooms/${buildingId}`);
      // Create a Set of unique floor values and convert back to array
      const uniqueFloors = [...new Set(response.data.map(room => room.floor))]
        .map(floor => ({
          _id: floor, // Using floor value as ID since we're grouping
          floor: floor,
        }))
        .sort((a, b) => a.floor - b.floor); // Sort floors numerically
      setFloors(uniqueFloors);
    } catch (error) {
      console.error(error);
    }
  };

  // Filter rooms for display
  // const displayRooms = rooms.filter(room => {
  //   const matchesCommunity = selectedCommunity && room.communityId === selectedCommunity;
  //   const matchesBuilding = !selectedBuilding || room.buildingId === selectedBuilding;
  //   const matchesFloor = selectedFloor === 'All Floor' || room.floor === selectedFloor;
  //   const matchesSip = selectedSipStatus === 'All SIP' ||
  //     (selectedSipStatus === 'Assigned SIP' && room.sipAccount) ||
  //     (selectedSipStatus === 'Unassigned SIP' && !room.sipAccount);

  //   return matchesCommunity && matchesBuilding && matchesFloor && matchesSip;
  // });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get('/settings/room-sip-settings', {
        communityId: selectedCommunity,
        buildingId: selectedBuilding,
        floorId: selectedFloor,
        sipStatus: selectedSipStatus,
      });
      setRooms(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveSip = async room => {
    try {
      await axios.post('/settings/remove-room-sip', { roomId: room._id });
      toast.success(t('SIP account removed successfully'));
      fetchRooms();
      setIsConfirmOpen(false);
      setRoomToRemoveSip(null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setDisplayRooms(
      rooms
        .filter(room => {
          const matchesCommunity = selectedCommunity && room.communityId === selectedCommunity;
          const matchesBuilding = !selectedBuilding || room.buildingId === selectedBuilding;
          const matchesFloor = selectedFloor === 'All Floor' || room.floor === selectedFloor;
          const matchesSip =
            selectedSipStatus === 'All SIP' ||
            (selectedSipStatus === 'Assigned SIP' && room.sipAccount) ||
            (selectedSipStatus === 'Unassigned SIP' && !room.sipAccount);
          const matchesSearch =
            searchKey.toLowerCase().trim() === '' ||
            (room?.communityName &&
              room.communityName.toLowerCase().includes(searchKey.toLowerCase())) ||
            (room?.buildingName &&
              room.buildingName.toLowerCase().includes(searchKey.toLowerCase())) ||
            (room?.floor &&
              room.floor.toString().toLowerCase().includes(searchKey.toLowerCase())) ||
            (room?.roomNumber &&
              room.roomNumber.toString().toLowerCase().includes(searchKey.toLowerCase())) ||
            (room?.sipAccount && room.sipAccount.toLowerCase().includes(searchKey.toLowerCase()));

          return matchesCommunity && matchesBuilding && matchesFloor && matchesSip && matchesSearch;
        })
        .sort((a, b) => {
          const roomA = (a?.roomNumber || '').toString().toLowerCase();
          const roomB = (b?.roomNumber || '').toString().toLowerCase();

          if (!roomA && roomB) return 1;
          if (roomA && !roomB) return -1;
          if (!roomA && !roomB) return 0;

          return roomA.localeCompare(roomB, undefined, { numeric: true, sensitivity: 'base' });
        })
    );
  }, [rooms, selectedCommunity, selectedBuilding, selectedFloor, selectedSipStatus, searchKey]);

  return (
    <div className="p-6 bg-gradient-to-br from-skin-base to-skin-secondary container-height app-text">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6 bg-skin-primary px-4 rounded-xl shadow-lg h-filter">
        <select
          value={selectedCommunity}
          onChange={e => {
            setSelectedCommunity(e.target.value);
            setSelectedBuilding('');
          }}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-filter-7"
        >
          <option value="" hidden>
            {t('Select Community')}
          </option>
          {communities.map(community => (
            <option key={community._id} value={community._id}>
              {community.communityName}
            </option>
          ))}
        </select>

        <select
          value={selectedBuilding}
          onChange={e => setSelectedBuilding(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-filter-7"
          disabled={!selectedCommunity}
        >
          <option value="">{t('all building')}</option>
          {buildings.map(building => (
            <option key={building._id} value={building._id}>
              {building.buildingName}
            </option>
          ))}
        </select>

        <select
          value={selectedFloor}
          onChange={e => setSelectedFloor(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          disabled={!selectedBuilding}
        >
          <option value="All Floor">{t('All Floor')}</option>
          {floors.map(floor => (
            <option key={floor._id} value={floor.floor}>
              {floor.floor}
            </option>
          ))}
        </select>

        <select
          value={selectedSipStatus}
          onChange={e => setSelectedSipStatus(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value="All SIP">{t('All SIP')}</option>
          <option value="Assigned SIP">{t('Assigned SIP')}</option>
          <option value="Unassigned SIP">{t('Unassigned SIP')}</option>
        </select>

        <div className="relative flex-1">
          <input
            type="text"
            placeholder={t('Search')}
            className="bg-skin-primary border border-skin-base/10 rounded-lg px-4 py-2 text-skin-base focus:outline-none focus:border-skin-accent transition-colors w-full"
            value={searchKey}
            onChange={e => setSearchKey(e.target.value)}
          />
          {searchKey && (
            <button
              onClick={() => {
                setSearchKey('');
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-skin-base/50 hover:text-skin-base"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="relative overflow-x-auto overflow-y-auto h-table-height">
        <table className="w-full">
          <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
            <tr className="border-b border-skin-base/10">
              <th className="sticky left-0 z-20 bg-skin-primary px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[50px]">
                {t('No.')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[200px]">
                {t('Community Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[200px]">
                {t('Building Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[100px]">
                {t('Floor')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">
                {t('Room No.')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">
                {t('SIP Account')}
              </th>
              <th className="sticky right-0 z-40 bg-skin-primary px-6 py-3 text-right text-sm font-semibold text-skin-base min-w-[100px]">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRooms.map((room, index) => (
              <tr key={room._id} className="border-t border-skin-base/10">
                <td className="sticky left-0 z-10 bg-skin-primary px-6 py-4 text-sm text-skin-base">
                  {index + 1}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {room.communityName}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {room.buildingName}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">{room.floor}</td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {room.roomNumber}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {room.sipAccount || '-'}
                </td>
                <td className="sticky right-0 z-10 bg-skin-primary px-6 py-4 text-center">
                  <div className="flex items-center gap-2 justify-end">
                    {room.sipAccount && (
                      <button
                        onClick={() => {
                          setRoomToRemoveSip(room);
                          setIsConfirmOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedRoom(room);
                        setIsAssignModalOpen(true);
                      }}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <FaCog className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <AssignSipModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedRoom(null);
        }}
        room={selectedRoom}
        onSuccess={() => {
          fetchRooms();
          setIsAssignModalOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setRoomToRemoveSip(null);
        }}
        onConfirm={() => handleRemoveSip(roomToRemoveSip)}
        title={t('Remove SIP Account')}
        message={t('Are you sure you want to remove this SIP account?')}
        confirmText={t('Remove')}
        cancelText={t('Cancel')}
        type="danger"
      />
    </div>
  );
};

export default RoomSipAccount;
